import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';

/* ── benefit data ── */
const benefits = [
  {
    title: '10x Faster Insights',
    desc: 'Collapse analysis timelines from weeks to minutes with automated AI workflows.',
    before: { label: 'Manual Reports', bars: [20, 35, 15, 40, 25] },
    after: { label: 'AI-Driven Dashboards', bars: [85, 92, 78, 95, 88] },
  },
  {
    title: '40% Cost Reduction',
    desc: 'Eliminate redundant tools and manual processes. One platform does it all.',
    before: { label: 'Multiple Tools', bars: [60, 45, 70, 50, 55] },
    after: { label: 'Unified Platform', bars: [30, 25, 35, 28, 30] },
  },
  {
    title: '98.7% Accuracy',
    desc: 'Industry-leading model performance validated across 50+ domains.',
    before: { label: 'Traditional Models', bars: [55, 60, 48, 52, 58] },
    after: { label: 'OpticVance AI', bars: [96, 98, 94, 99, 97] },
  },
  {
    title: 'Zero Downtime',
    desc: '99.99% uptime with auto-scaling infrastructure and global CDN.',
    before: { label: 'Legacy Infra', bars: [70, 40, 80, 35, 65] },
    after: { label: 'Auto-Scaling', bars: [99, 99, 98, 99, 99] },
  },
  {
    title: 'Self-Learning Models',
    desc: 'Models improve continuously without manual retraining.',
    before: { label: 'Static Training', bars: [65, 60, 55, 58, 50] },
    after: { label: 'Continuous Learning', bars: [75, 82, 88, 92, 97] },
  },
  {
    title: 'ROI in 30 Days',
    desc: 'See measurable impact within the first month of deployment.',
    before: { label: '6-Month Timeline', bars: [10, 15, 20, 30, 45] },
    after: { label: '30-Day Impact', bars: [40, 65, 80, 90, 95] },
  },
];

/* ── Mini bar chart for before/after visuals ── */
const MiniChart = ({ bars, color, animate }) => (
  <div className="flex items-end gap-1.5 h-16">
    {bars.map((h, i) => (
      <motion.div
        key={i}
        className="flex-1 rounded-sm"
        style={{ backgroundColor: color }}
        initial={{ height: 0 }}
        animate={animate ? { height: `${h}%` } : { height: `${h}%` }}
        transition={{ duration: 0.6, delay: i * 0.08 }}
      />
    ))}
  </div>
);

/* ── Before/After slider ── */
const BeforeAfterSlider = ({ benefit }) => {
  const [pos, setPos] = useState(50);
  const [hovered, setHovered] = useState(false);

  const handleMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(x);
  }, []);

  return (
    <div
      className="relative h-32 rounded-xl overflow-hidden cursor-col-resize select-none"
      onMouseMove={handleMove}
      onTouchMove={handleMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="slider"
      aria-label={`Compare ${benefit.before.label} vs ${benefit.after.label}`}
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {/* After layer (full background) */}
      <div className="absolute inset-0 bg-dark-card p-4">
        <div className="text-[10px] font-mono text-secondary/80 mb-2 uppercase tracking-wider">{benefit.after.label}</div>
        <MiniChart bars={benefit.after.bars} color="#54E346" animate={hovered} />
      </div>

      {/* Before layer (clipped) */}
      <div
        className="absolute inset-0 bg-black p-4"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <div className="text-[10px] font-mono text-gray-500 mb-2 uppercase tracking-wider">{benefit.before.label}</div>
        <MiniChart bars={benefit.before.bars} color="#333333" animate={false} />
        <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      </div>

      {/* Slider handle */}
      <motion.div
        className="absolute top-0 bottom-0 w-0.5 bg-primary z-10"
        style={{ left: `${pos}%` }}
        animate={{ boxShadow: hovered ? '0 0 12px rgba(8,203,0,0.5)' : '0 0 4px rgba(8,203,0,0.2)' }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-dark border-2 border-primary flex items-center justify-center">
          <svg viewBox="0 0 12 12" className="w-3 h-3 text-primary" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 4l-2 2 2 2M9 4l2 2-2 2" />
          </svg>
        </div>
      </motion.div>
    </div>
  );
};

/* ── Desktop benefit row ── */
const BenefitRow = ({ benefit, index }) => (
  <motion.div
    className="grid lg:grid-cols-2 gap-6 items-center py-6 border-b border-dark-border/50 last:border-0"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
  >
    {/* Left: checkmark + text */}
    <div className="flex gap-4 items-start">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center mt-0.5">
        <span className="text-secondary">{icons.check}</span>
      </div>
      <div>
        <h3 className="text-lg font-bold mb-1">{benefit.title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{benefit.desc}</p>
      </div>
    </div>

    {/* Right: before/after slider */}
    <BeforeAfterSlider benefit={benefit} />
  </motion.div>
);

/* ── Mobile accordion panel ── */
const AccordionPanel = ({ benefit, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className="glass-card rounded-xl overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <span className="flex-shrink-0 text-secondary">{icons.check}</span>
        <span className="flex-1 font-semibold text-sm">{benefit.title}</span>
        <motion.svg
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className="w-4 h-4 text-primary/50 flex-shrink-0"
          animate={{ rotate: open ? 180 : 0 }}
        >
          <path d="M6 9l6 6 6-6" />
        </motion.svg>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3">
              <p className="text-gray-400 text-sm leading-relaxed">{benefit.desc}</p>
              <BeforeAfterSlider benefit={benefit} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ── Main ── */
const Benefits = () => (
  <Section id="benefits" className="py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        tag="Benefits"
        title={<>Why Leaders Choose <span className="gradient-text">OpticVance</span></>}
        desc="Tangible outcomes that transform your data strategy from a cost center into a competitive advantage."
      />

      {/* Desktop: comparison table */}
      <div className="hidden lg:block glass-card rounded-2xl p-8">
        <div className="grid grid-cols-2 gap-6 pb-4 mb-4 border-b border-dark-border/50">
          <span className="text-xs font-mono uppercase tracking-widest text-gray-500">Benefit</span>
          <span className="text-xs font-mono uppercase tracking-widest text-gray-500">Before → After</span>
        </div>
        {benefits.map((b, i) => (
          <BenefitRow key={b.title} benefit={b} index={i} />
        ))}
      </div>

      {/* Mobile: accordion panels */}
      <div className="lg:hidden space-y-3">
        {benefits.map((b, i) => (
          <AccordionPanel key={b.title} benefit={b} index={i} />
        ))}
      </div>
    </div>
  </Section>
);

export default Benefits;
